<template>
  <v-card class="stripe-account-card" :loading="useAccountStore().isLinkLoading">
    <v-card-item class="pb-1">
      <template v-slot:prepend>
        <v-avatar color="primary" variant="tonal" size="40">
          <v-icon icon="mdi-bank" size="22" />
        </v-avatar>
      </template>
      <v-card-title>
        {{ $t("page.account.stripeAccount") }}
      </v-card-title>
    </v-card-item>

    <v-divider />

    <v-card-text class="pt-3 pb-2">
      <div class="account-id-panel mb-3">
        <div class="app-field-label mb-1">Stripe account ID</div>
        <div class="d-flex align-center justify-space-between ga-3">
          <span class="account-id text-truncate">{{ account?.id || "—" }}</span>
          <v-btn
            icon="mdi-content-copy"
            size="x-small"
            variant="text"
            :disabled="!account?.id"
            @click="account?.id && copyToClipboard(account.id)"
          />
        </div>
      </div>

      <div class="account-capabilities">
        <div class="capability-row">
          <div>
            <div class="app-field-label mb-1">Status</div>
            <div class="capability-helper">Stripe account review and onboarding state</div>
          </div>
          <account-status-chip />
        </div>

        <v-divider />

        <div class="capability-row">
          <div>
            <div class="app-field-label mb-1">Charges</div>
            <div class="capability-helper">Accept customer payments</div>
          </div>
          <v-chip
            :color="account?.charges_enabled ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{
              account?.charges_enabled ? $t("common.status.enabled") : $t("common.status.disabled")
            }}
          </v-chip>
        </div>

        <v-divider />

        <div class="capability-row">
          <div>
            <div class="app-field-label mb-1">Payouts</div>
            <div class="capability-helper">Transfer funds to your bank</div>
          </div>
          <v-chip
            :color="account?.payouts_enabled ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{
              account?.payouts_enabled ? $t("common.status.enabled") : $t("common.status.disabled")
            }}
          </v-chip>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="px-4 pb-3 pt-0 justify-end">
      <v-btn
        v-if="hasRequirementsOrDisabledFeatures"
        :loading="useAccountStore().isLinkLoading"
        color="primary"
        append-icon="mdi-open-in-new"
        @click="openStripeOnboarding"
      >
        {{ $t("page.account.completeOnboarding") }}
      </v-btn>
      <v-btn v-else color="primary" append-icon="mdi-open-in-new" @click="openStripeDashboard">
        {{ $t("page.account.gotoDashboard") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts" setup>
import AccountStatusChip from "@/account/components/account/AccountStatusChip.vue";
import config from "@/app/configs";
import { useAccountStore } from "@/account/stores/account";
import { copyToClipboard } from "@/shared/utils/copyToClipboard.utils";
import { computed, ref } from "vue";

const accountStore = useAccountStore();
const accountLink = ref<{ url?: string } | undefined>(undefined);
const account = computed(() => accountStore.account);
const accountLinkReturn = ref<string>(config?.links?.accountPageReturn ?? "");

const hasRequirementsOrDisabledFeatures = computed(() => {
  const requirements = account.value?.requirements;

  return (
    (requirements?.eventually_due?.length ?? 0) !== 0 ||
    (requirements?.currently_due?.length ?? 0) !== 0 ||
    account.value?.charges_enabled === false ||
    account.value?.payouts_enabled === false
  );
});

const openStripeDashboard = () => {
  if (account.value?.id) {
    window.open(`https://dashboard.stripe.com/${account.value.id}`, "_blank");
  }
};

const openStripeOnboarding = async () => {
  const onboardingWindow = window.open("", "_blank");
  accountLink.value = await accountStore.createAccountLink(accountLinkReturn.value);

  if (accountLink.value?.url) {
    if (onboardingWindow) {
      onboardingWindow.location.href = accountLink.value.url;
    } else {
      window.open(accountLink.value.url, "_blank");
    }
  } else {
    onboardingWindow?.close();
  }
};
</script>

<style scoped>
.stripe-account-card {
  width: 100%;
  min-height: 13rem;
}

.account-id-panel {
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.account-id {
  color: rgba(var(--v-theme-on-surface), 0.86);
  font-family: "Courier New", Courier, monospace;
  font-size: 0.9rem;
  font-weight: 700;
}

.account-capabilities {
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 12px;
}

.capability-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.capability-helper {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.82rem;
  line-height: 1.2;
}

@media (max-width: 599px) {
  .capability-row {
    grid-template-columns: 1fr;
    justify-items: start;
  }
}
</style>
